// Salon-local time formatting.
// The backend emits NAIVE ISO strings (e.g. "2026-02-10T14:00:00") that
// represent salon-local wall-clock times. We read those strings as-is —
// no `new Date()` parsing, because that would apply the browser's
// timezone offset and shift the displayed time.

const RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

function parts(iso) {
  if (!iso) return null;
  // Strip any tz suffix the legacy payload might still carry; we only
  // care about the wall-clock fields.
  const s = String(iso).replace(/Z|[+-]\d{2}:?\d{2}$/i, "");
  const m = RE.exec(s);
  if (!m) return null;
  return {
    year: +m[1], month: +m[2], day: +m[3],
    hour: +m[4], minute: +m[5], second: +(m[6] || 0),
  };
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAYS_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function pad(n) { return String(n).padStart(2, "0"); }

function ampm(hour, minute) {
  const period = hour >= 12 ? "PM" : "AM";
  const h = ((hour + 11) % 12) + 1; // 0→12, 13→1
  return `${h}:${pad(minute)} ${period}`;
}

// "2:30 PM"
export function formatTime(iso) {
  const p = parts(iso);
  if (!p) return "";
  return ampm(p.hour, p.minute);
}

// "Mon Apr 14, 2:30 PM"
export function formatDateTime(iso) {
  const p = parts(iso);
  if (!p) return "";
  // Weekday name requires a date object, but a UTC-anchored date keeps
  // the wall-clock fields exactly as given.
  const wd = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  return `${WEEKDAYS_SHORT[wd]} ${MONTHS[p.month - 1]} ${p.day}, ${ampm(p.hour, p.minute)}`;
}

// "Apr 14"
export function formatDate(iso) {
  const p = parts(iso);
  if (!p) return "";
  return `${MONTHS[p.month - 1]} ${p.day}`;
}

// Local YYYY-MM-DD from a Date (no UTC shift). For querying day windows.
export function isoDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Naive ISO datetime ("YYYY-MM-DDTHH:MM:SS") from a Date — wall-clock,
// no tz conversion. Pair this with the backend's lenient parser.
export function naiveISO(d, hour = 0, minute = 0) {
  const dt = new Date(d);
  dt.setHours(hour, minute, 0, 0);
  return (
    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}` +
    `T${pad(dt.getHours())}:${pad(dt.getMinutes())}:00`
  );
}

// Hour/minute extracted from a naive ISO — used by the day-grid calendar.
export function hoursMinutes(iso) {
  const p = parts(iso);
  return p ? { hour: p.hour, minute: p.minute } : { hour: 0, minute: 0 };
}

// Returns true iff the wall-clock date in `iso` equals the given Date's
// local day. Used by Dashboard/Calendar to bucket bookings by day.
export function isSameDay(iso, dateObj) {
  const p = parts(iso);
  if (!p) return false;
  return (
    p.year === dateObj.getFullYear() &&
    p.month === dateObj.getMonth() + 1 &&
    p.day === dateObj.getDate()
  );
}

// For sort comparators — returns a comparable number from a naive ISO.
export function isoOrder(iso) {
  const p = parts(iso);
  if (!p) return 0;
  return ((p.year * 10000 + p.month * 100 + p.day) * 10000) + p.hour * 100 + p.minute;
}
